// ─── Sample Sales Demo Data ───────────────────────────────────────────────────
// Used for guest/demo mode — no backend required

const PRODUCTS = ["Laptop","Mouse","Keyboard","Monitor","Headphones","Tablet","Phone","Speaker","Camera","Printer"];
const CATEGORIES: Record<string, string> = {
  Laptop:"Computers", Mouse:"Accessories", Keyboard:"Accessories",
  Monitor:"Computers", Headphones:"Audio", Tablet:"Mobile",
  Phone:"Mobile", Speaker:"Audio", Camera:"Imaging", Printer:"Computers",
};
const REGIONS = ["North","South","East","West","Central","Northeast","Northwest","Southeast","Southwest"];
const CUSTOMER_TYPES = ["Business","Individual","Education","Government","Healthcare"];
const PAYMENT_METHODS = ["Credit Card","Debit Card","Cash","Bank Transfer","Digital Wallet"];
const BASE_PRICES: Record<string, number> = {
  Laptop:1200, Mouse:35, Keyboard:80, Monitor:350, Headphones:150,
  Tablet:500, Phone:800, Speaker:120, Camera:600, Printer:200,
};

// Product-specific base margins — creates real differentiation in the chart
const BASE_MARGINS: Record<string, number> = {
  Headphones: 0.31,  // premium audio, high margin
  Camera:     0.29,  // imaging, strong margin
  Tablet:     0.27,  // mobile, decent margin
  Monitor:    0.25,  // display, average
  Phone:      0.24,  // competitive market
  Laptop:     0.22,  // high revenue but thin margin
  Speaker:    0.20,  // commodity audio
  Keyboard:   0.19,  // accessory, low margin
  Mouse:      0.18,  // commodity accessory
  Printer:    0.16,  // lowest margin — ink is where they make money
};

function seededRand(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    return ((s >>> 0) / 0xffffffff);
  };
}

export interface SaleRow {
  date: string;
  product: string;
  category: string;
  region: string;
  sales: number;
  quantity: number;
  customer_type: string;
  profit_margin: number;
  discount: number;
  payment_method: string;
}

export function generateDemoData(count = 1000): SaleRow[] {
  const rand = seededRand(42);
  const rows: SaleRow[] = [];
  const startMs = new Date("2024-01-01").getTime();
  const endMs   = new Date("2024-12-31").getTime();

  for (let i = 0; i < count; i++) {
    const product = PRODUCTS[Math.floor(rand() * PRODUCTS.length)];
    const region  = REGIONS[Math.floor(rand() * REGIONS.length)];
    const qty     = Math.floor(rand() * 10) + 1;
    const base    = BASE_PRICES[product];
    let price     = base * qty * (0.85 + rand() * 0.3);
    if (["Business","Government"].includes(CUSTOMER_TYPES[Math.floor(rand() * CUSTOMER_TYPES.length)])) price *= 1.15;
    if (["North","Northeast"].includes(region)) price *= 1.2;
    const dateMs  = startMs + Math.floor(rand() * (endMs - startMs));
    const date    = new Date(dateMs).toISOString().slice(0, 10);

    rows.push({
      date,
      product,
      category: CATEGORIES[product],
      region,
      sales: Math.round(price),
      quantity: qty,
      customer_type: CUSTOMER_TYPES[Math.floor(rand() * CUSTOMER_TYPES.length)],
      profit_margin: Math.round(Math.max(0.05, Math.min(0.45, BASE_MARGINS[product] + (rand() * 0.08 - 0.04))) * 100) / 100,
      discount: [0, 5, 10, 15, 20][Math.floor(rand() * 5)],
      payment_method: PAYMENT_METHODS[Math.floor(rand() * PAYMENT_METHODS.length)],
    });
  }
  return rows;
}

export const DEMO_USER = { id: -1, username: "Demo User", role: "guest" };
export const DEMO_PROJECT = { id: -1, name: "Sample Sales Data", user_id: -1, created_at: "2024-01-01T00:00:00", dataset_count: 1 };
export const DEMO_DATASET = { id: -1, name: "sales_2024.csv", table_name: "demo_sales", project_id: -1, uploaded_at: "2024-01-01T00:00:00" };

// ─── Companion Dataset 1: Product Catalog ─────────────────────────────────────
// 10 rows — one per product — join key: `product`
export interface ProductCatalogRow {
  product: string;
  supplier: string;
  country_of_origin: string;
  launch_year: number;
  warranty_years: number;
  avg_rating: number;
  in_stock: string;
}

export function generateProductCatalog(): ProductCatalogRow[] {
  return [
    { product: "Laptop",     supplier: "TechCorp Ltd",   country_of_origin: "Taiwan",      launch_year: 2022, warranty_years: 2, avg_rating: 4.6, in_stock: "Yes" },
    { product: "Mouse",      supplier: "PeriphX Inc",    country_of_origin: "China",        launch_year: 2023, warranty_years: 1, avg_rating: 4.3, in_stock: "Yes" },
    { product: "Keyboard",   supplier: "PeriphX Inc",    country_of_origin: "China",        launch_year: 2022, warranty_years: 1, avg_rating: 4.5, in_stock: "Yes" },
    { product: "Monitor",    supplier: "DisplayPro",     country_of_origin: "South Korea",  launch_year: 2021, warranty_years: 3, avg_rating: 4.7, in_stock: "Yes" },
    { product: "Headphones", supplier: "AudioWave",      country_of_origin: "Germany",      launch_year: 2023, warranty_years: 2, avg_rating: 4.8, in_stock: "No"  },
    { product: "Tablet",     supplier: "TechCorp Ltd",   country_of_origin: "Taiwan",       launch_year: 2022, warranty_years: 1, avg_rating: 4.4, in_stock: "Yes" },
    { product: "Phone",      supplier: "MobileGen",      country_of_origin: "South Korea",  launch_year: 2023, warranty_years: 1, avg_rating: 4.5, in_stock: "Yes" },
    { product: "Speaker",    supplier: "AudioWave",      country_of_origin: "Japan",        launch_year: 2021, warranty_years: 2, avg_rating: 4.2, in_stock: "Yes" },
    { product: "Camera",     supplier: "LensMaster",     country_of_origin: "Japan",        launch_year: 2020, warranty_years: 2, avg_rating: 4.6, in_stock: "No"  },
    { product: "Printer",    supplier: "OfficeNet",      country_of_origin: "China",        launch_year: 2021, warranty_years: 1, avg_rating: 4.0, in_stock: "Yes" },
  ];
}

// ─── Companion Dataset 2: Regional Targets ────────────────────────────────────
// 9 rows — one per region — join key: `region`
export interface RegionTargetRow {
  region: string;
  sales_target_2024: number;
  sales_rep_count: number;
  territory_manager: string;
  market_size: string;
}

export function generateRegionTargets(): RegionTargetRow[] {
  return [
    { region: "North",     sales_target_2024: 500000, sales_rep_count: 12, territory_manager: "Alice Chen",      market_size: "Large"  },
    { region: "South",     sales_target_2024: 420000, sales_rep_count: 10, territory_manager: "Bob Martinez",    market_size: "Large"  },
    { region: "East",      sales_target_2024: 380000, sales_rep_count:  9, territory_manager: "Carol Singh",     market_size: "Medium" },
    { region: "West",      sales_target_2024: 450000, sales_rep_count: 11, territory_manager: "David Park",      market_size: "Large"  },
    { region: "Central",   sales_target_2024: 290000, sales_rep_count:  7, territory_manager: "Eve Johnson",     market_size: "Medium" },
    { region: "Northeast", sales_target_2024: 360000, sales_rep_count:  9, territory_manager: "Frank Liu",       market_size: "Medium" },
    { region: "Northwest", sales_target_2024: 310000, sales_rep_count:  8, territory_manager: "Grace Williams",  market_size: "Small"  },
    { region: "Southeast", sales_target_2024: 340000, sales_rep_count:  8, territory_manager: "Henry Brown",     market_size: "Medium" },
    { region: "Southwest", sales_target_2024: 280000, sales_rep_count:  7, territory_manager: "Iris Taylor",     market_size: "Small"  },
  ];
}
